import { useState, useEffect } from 'react';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

export default function AudiobooksPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedBook, setSelectedBook] = useState('');
  const [selectedOption, setSelectedOption] = useState('');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({ option: '', email: '' });
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);
  const [showCanceledBanner, setShowCanceledBanner] = useState(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('success') === 'true') {
      setShowSuccessBanner(true);
      setTimeout(() => setShowSuccessBanner(false), 8000);
      window.history.replaceState({}, '', '/audiobooks');
    }
    if (urlParams.get('canceled') === 'true') {
      setShowCanceledBanner(true);
      setTimeout(() => setShowCanceledBanner(false), 6000);
      window.history.replaceState({}, '', '/audiobooks');
    }
  }, []);

  const audiobooks = [
    {
      title: 'Nine Lives',
      duration: '6 minutes 35 seconds',
      description: 'Experience the transformative journey of Nine Lives through narration that captures the emotional depth and rhythmic flow of each poem. This audiobook brings the collection to life with carefully crafted vocal performance.',
      image: 'https://static.readdy.ai/image/09a869d581523d35fa4e4396fbda86f5/f0ebce1b657b010086914c46f7f716ad.png',
      libroFmUrl: 'https://libro.fm/audiobooks/9798240118524-nine-lives',
      readAloudUrl: 'https://theartofkent.gumroad.com/l/Nine_Lives'
    },
    {
      title: 'Meta-Postal Expression',
      duration: '6 minutes 37 seconds',
      description: 'Listen to Meta Postal Expression as each poem unfolds like a message being delivered. The narration emphasizes the metaphorical language and reflective nature of this unique collection.',
      image: 'https://static.readdy.ai/image/09a869d581523d35fa4e4396fbda86f5/d613a552260aaa1dd9345a9794a92cf9.png',
      libroFmUrl: 'https://libro.fm/audiobooks/9798240121678-meta-postal-expression',
      readAloudUrl: 'https://theartofkent.gumroad.com/l/Meta-Postal_Expression'
    },
    {
      title: 'Silkfoot',
      duration: '22 minutes 8 seconds',
      description: 'Silkfoot comes alive through gentle, soulful narration that honors the delicate rhythm and introspective nature of the poetry. Perfect for quiet moments of reflection.',
      image: 'https://static.readdy.ai/image/09a869d581523d35fa4e4396fbda86f5/9717d8eaf0a17c267e0852cfb954c6b9.png',
      libroFmUrl: 'https://libro.fm/audiobooks/9798240117824',
      readAloudUrl: 'https://theartofkent.gumroad.com/l/Silkfoot'
    }
  ];

  const platforms = [
    { name: 'Libro.fm', icon: 'ri-headphone-line' },
    { name: 'Apple Books', icon: 'ri-apple-line' },
    { name: 'Google Play', icon: 'ri-google-play-line' },
    { name: 'Spotify', icon: 'ri-spotify-line' }
  ];

  const openModal = (bookTitle: string) => {
    setSelectedBook(bookTitle);
    setIsModalOpen(true);
    setSelectedOption('');
    setEmail('');
    setErrors({ option: '', email: '' });
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedOption('');
    setEmail('');
    setErrors({ option: '', email: '' });
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const newErrors = { option: '', email: '' };
    
    if (!selectedOption) {
      newErrors.option = 'Please select an option';
    }
    
    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    setErrors(newErrors);
    
    if (newErrors.option || newErrors.email) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: {
          bookTitle: selectedBook,
          format: selectedOption,
          customerEmail: email,
          siteOrigin: window.location.origin,
        },
      });

      if (error) {
        console.error('Error creating checkout session:', error);
        alert('Failed to create checkout session. Please try again.');
        return;
      }

      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Error:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Success Banner */}
      {showSuccessBanner && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 max-w-md w-full mx-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 shadow-lg">
            <div className="flex items-start">
              <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                <i className="ri-check-line text-xl text-green-600"></i>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-green-900">Purchase Successful!</h3>
                <p className="text-sm text-green-700 mt-1">Your audiobook purchase was completed. Check your email for details.</p>
              </div>
              <button
                onClick={() => setShowSuccessBanner(false)}
                className="ml-3 w-5 h-5 flex items-center justify-center text-green-400 hover:text-green-600 cursor-pointer"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Canceled Banner */}
      {showCanceledBanner && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 max-w-md w-full mx-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 shadow-lg">
            <div className="flex items-start">
              <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                <i className="ri-information-line text-xl text-amber-600"></i>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-amber-900">Payment Canceled</h3>
                <p className="text-sm text-amber-700 mt-1">No worries! Feel free to try again whenever you're ready.</p>
              </div>
              <button
                onClick={() => setShowCanceledBanner(false)}
                className="ml-3 w-5 h-5 flex items-center justify-center text-amber-400 hover:text-amber-600 cursor-pointer"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="pt-32 pb-24 px-6 bg-stone-50">
        <div className="max-w-5xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-light text-gray-900 mb-6 tracking-wide">
            Experience the Poetry Through Sound
          </h1>
          <p className="text-base md:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Each audiobook is carefully narrated to enhance the rhythm, emotion, and atmosphere of Kent's poetry. Designed for listeners who want to feel the poetry, not just read it.
          </p>
          <div className="w-24 h-1 bg-amber-700 mx-auto mt-8"></div>
        </div>
      </section>

      {/* Audiobooks Section */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-6xl mx-auto space-y-16">
          {audiobooks.map((audiobook, index) => (
            <div 
              key={index}
              className={`grid md:grid-cols-2 gap-12 items-center ${index === 1 ? 'md:grid-flow-dense' : ''}`}
            >
              <div className={index === 1 ? 'md:order-2' : ''}>
                <div className="w-full h-96 rounded-lg overflow-hidden shadow-xl">
                  <img 
                    src={audiobook.image}
                    alt={audiobook.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
              <div className={index === 1 ? 'md:order-1' : ''}>
                <h3 className="text-3xl md:text-4xl font-light text-gray-900 mb-4 tracking-wide">
                  {audiobook.title}
                </h3>
                <p className="text-sm text-amber-700 mb-6 uppercase tracking-widest">
                  Duration: {audiobook.duration}
                </p>
                <p className="text-base text-gray-600 leading-relaxed mb-8">
                  {audiobook.description}
                </p>

                <div className="flex flex-wrap gap-4">
                  {audiobook.libroFmUrl ? (
                    <a
                      href={audiobook.libroFmUrl}
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="inline-block px-8 py-3 bg-amber-700 text-white text-sm font-light tracking-widest uppercase hover:bg-amber-800 transition-all duration-300 whitespace-nowrap cursor-pointer"
                    >
                      Purchase Audiobook
                    </a>
                  ) : (
                    <button 
                      onClick={() => openModal(audiobook.title)}
                      className="inline-block px-8 py-3 bg-amber-700 text-white text-sm font-light tracking-widest uppercase hover:bg-amber-800 transition-all duration-300 whitespace-nowrap cursor-pointer"
                    >
                      Purchase Audiobook
                    </button>
                  )}
                  {audiobook.readAloudUrl && (
                    <a
                      href={audiobook.readAloudUrl}
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="inline-block px-8 py-3 bg-amber-700 text-white text-sm font-light tracking-widest uppercase hover:bg-amber-800 transition-all duration-300 whitespace-nowrap cursor-pointer"
                    >
                      Purchase Read Aloud Audiobook Video
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Available On Section */}
      <section className="py-24 px-6 bg-stone-50">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-light text-gray-900 mb-6 tracking-wide">
            Available On
          </h2>
          <p className="text-base text-gray-600 mb-12">
            Listen to Kent's audiobooks on your favorite platform
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {platforms.map((platform, index) => (
              <div
                key={index}
                className="bg-white rounded-lg p-8 shadow-lg"
              >
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4 text-amber-700">
                  <i className={`${platform.icon} text-4xl`}></i>
                </div>
                <p className="text-sm font-light text-gray-900">{platform.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-light text-gray-900 mb-6 tracking-wide">
            Explore the Written Word
          </h2>
          <p className="text-base md:text-lg text-gray-600 mb-12 leading-relaxed">
            Prefer to read? Discover the poetry collections in print and digital formats.
          </p>
          <a 
            href="/books"
            className="inline-block px-10 py-4 bg-gray-900 text-white text-sm font-light tracking-widest uppercase hover:bg-gray-800 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            View Books
          </a>
        </div>
      </section>

      {/* Purchase Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-6">
          <div className="bg-white rounded-lg max-w-md w-full p-8 relative">
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
            
            <h3 className="text-2xl font-light text-gray-900 mb-2 tracking-wide">
              Purchase Audiobook
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              {selectedBook}
            </p>
            
            <form onSubmit={handleSubmit}>
              <div className="mb-6">
                <label className="block text-sm font-light text-gray-900 mb-3">
                  Select Format <span className="text-red-500">*</span>
                </label>
                <div className="space-y-3">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="option"
                      value="Audiobook"
                      checked={selectedOption === 'Audiobook'}
                      onChange={(e) => setSelectedOption(e.target.value)}
                      className="w-4 h-4 text-amber-700 cursor-pointer"
                    />
                    <span className="ml-3 text-sm text-gray-700">
                      Audiobook ({selectedBook === 'Silkfoot' ? '$14.99' : '$9.99'})
                    </span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="option"
                      value="Video Version (with Subtitles)"
                      checked={selectedOption === 'Video Version (with Subtitles)'}
                      onChange={(e) => setSelectedOption(e.target.value)}
                      className="w-4 h-4 text-amber-700 cursor-pointer"
                    />
                    <span className="ml-3 text-sm text-gray-700">
                      Video Version with Subtitles ({selectedBook === 'Silkfoot' ? '$19.99' : '$14.99'})
                    </span>
                  </label>
                </div>
                {errors.option && (
                  <p className="text-xs text-red-500 mt-2">{errors.option}</p>
                )}
              </div>
              
              <div className="mb-6">
                <label htmlFor="email" className="block text-sm font-light text-gray-900 mb-2">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-amber-700"
                  placeholder="your@email.com"
                />
                {errors.email && (
                  <p className="text-xs text-red-500 mt-2">{errors.email}</p>
                )}
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-3 bg-amber-700 text-white text-sm font-light tracking-widest uppercase hover:bg-amber-800 transition-all duration-300 whitespace-nowrap cursor-pointer disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Processing...
                  </>
                ) : (
                  'Proceed to Checkout'
                )}
              </button>
              
              <p className="text-xs text-gray-500 text-center mt-4">
                Secure payment powered by Stripe
              </p>
            </form>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}